import { useEffect, useReducer } from 'react'
import {
    Autocomplete,
    Button,
    TextField,
    FormControlLabel,
    Chip,
    Tooltip,
    Switch,
    Typography,
    Box,
    FormControl,
} from '@mui/material'
import {
    Star,
    StarBorderOutlined,
    Save as SaveIcon,
    RestartAlt as RestartAltIcon,
} from '@mui/icons-material'
import {
    SaveButton,
    Grid,
    FormSection,
    UniversalGoogleMapsAc,
    Autocomplete as UniversalAutocomplete,
    UniversalAccordion,
} from '../../../UniversalComponents'

import { useToastMessage } from '../../../../context/ToastPopUpContext'
import { useMinimizer } from '../../../../context/MinimizeContext'
import { useAuth } from '../../../../context/AuthenticationContext'
import { useNavMenu } from '../../../../context/NavMenuContext'
import responseError from '../../../../context/responseError/responseError'
import {
    editUserPassword,
    getDealers,
    getUserProfiles,
    getManufacturers,
    addUser,
    editUser,
    getBoats,
    getBoatModels,
    getUsers,
    userRegistrationDeactivation,
} from '../../../../apiRoutes'

import UserProfileForm from '../UserProfiles/UserProfileForm'
import parsePhoneNumber, { AsYouType } from 'libphonenumber-js'
import { useGlobalState } from '../../../../context/GlobalStateContext'

interface UserRegistrationProps {
    row: any
    permissionCheck?: boolean
    onClose?: () => void
    userAccount?: boolean
}

const UserRegistrationForm = ({
    row,
    onClose = () => {},
    userAccount,
    permissionCheck,
}: UserRegistrationProps) => {
    const setToastMessage = useToastMessage()
    const currentUser = useAuth()
    const auth: any = currentUser.auth
    const { globalState } = useGlobalState()
    const { oupUser } = globalState

    const defaultState = {
        formData: {
            userEmail: row.userEmail || '',
            userFirstName: row.userFirstName || '',
            userLastName: row.userLastName || '',
            userPhoneNumber: row.userPhoneNumber || '',
            userAddress: row?.userAddress || '',
            password: '',
            manufacturer: row.manufacturerName || [],
            userProfile: row?.userProfile || {},
            fullHin: row.fullHin || '',
            boatModel: row.model || '',
            assignedDealer: row?.assignedDealer?.dealerName || '',
            activeManufacturer: '',
        },
        formValidation: {
            userEmailValidation: false,
            passwordValidation: false,
            userFirstNameValidation: false,
            userLastNameValidation: false,
            userPhoneNumberValidation: false,
            fullHinValidation: false,
            boatModelValidation: false,
            manufacturerValidation: false,
            userProfileValidation: false,
            assignedDealerValidation: false,
            activeManufacturerValidation: false,
        },
        phoneNumberError: false,
        fullHinCharacterCount: '',
        boatModels: [],
        boatModelOptions: [],
        boatManufacturers: [],
        boatManufacturerOptions: [],
        userProfiles: [],
        dealers: [],
        dealerOptions: [],
        assignedManufacturersOptions: [],
        assignedManufacturers: [],
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const {
        formData,
        formValidation,
        phoneNumberError,
        fullHinCharacterCount,
        boatModels,
        boatModelOptions,
        boatManufacturers,
        boatManufacturerOptions,
        userProfiles,
        dealers,
        dealerOptions,
        assignedManufacturersOptions,
        assignedManufacturers,
    } = state
    const {
        userEmail,
        userFirstName,
        userLastName,
        userPhoneNumber,
        userAddress,
        password,
        manufacturer,
        activeManufacturer,
        userProfile,
        fullHin,
        boatModel,
        assignedDealer,
    } = formData

    const {
        userEmailValidation,
        passwordValidation,
        userFirstNameValidation,
        userLastNameValidation,
        userPhoneNumberValidation,
        fullHinValidation,
        boatModelValidation,
        manufacturerValidation,
        userProfileValidation,
        assignedDealerValidation,
        activeManufacturerValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    const getData = async () => {
        let boatModels: any = []
        let boatModelOptions: any = []
        let boatManufacturers: any = []
        let boatManufacturerOptions: any = []
        let userProfiles: any = []
        let dealers: any = []
        let dealerOptions: any = []
        try {
            const getBoatModelData = await getBoatModels(true)
            const getBoatManufacturerData = await getManufacturers(true)
            const userProfilesData = await getUserProfiles(true)
            const getDealerData = await getDealers(true)

            if (getBoatModelData && getBoatModelData.length !== 0) {
                boatModels = getBoatModelData

                boatModelOptions = getBoatModelData
                    .sort((a, b) => b.modelYear - a.modelYear)
                    .map((model) => `${model.modelYear} - ${model.model}`)
            }
            if (
                getBoatManufacturerData &&
                getBoatManufacturerData.length !== 0
            ) {
                boatManufacturers = getBoatManufacturerData.filter(
                    (manufacturer) => manufacturer.manufacturerType === 'boat'
                )
                boatManufacturerOptions = boatManufacturers.map(
                    ({ manufacturerName }) => manufacturerName
                )
            }
            if (userProfilesData && userProfilesData?.length !== 0) {
                userProfiles = userProfilesData.map(
                    (userProfile) => userProfile
                )
            }
            if (getDealerData && getDealerData.length !== 0) {
                dealers = getDealerData
                dealerOptions = dealers.map((dealer) => dealer?.dealerName)
            }
            setState({
                boatModels,
                boatModelOptions,
                boatManufacturers,
                boatManufacturerOptions,
                userProfiles,
                dealers,
                dealerOptions,
            })
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target

        if (name === 'userProfile') {
            setState({
                formData: {
                    ...formData,
                    [name]: userProfiles.find(
                        (userProfile) => userProfile.userProfileName === value
                    ),
                    activeManufacturer: '',
                },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formData: { ...formData, [name]: value },
            })
        }
    }

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }

    const handleSubmit = async () => {
        try {
            const findDealer = dealers.find(
                ({ dealerName }) => dealerName === assignedDealer
            )
            const findActiveManufacturer = assignedManufacturers?.find(
                ({ manufacturerName }) =>
                    manufacturerName === activeManufacturer
            )

            const parsedFormData = {
                ...formData,
                activeManufacturer: findActiveManufacturer,
                assignedDealer: findDealer || null,
                manufacturer: findActiveManufacturer,
                userBoats: row?.boat,
            }

            const updateUserActivation = {
                ...formData,
                manufacturer: findActiveManufacturer,
                active: false,
                id: row?.id,
            }

            let res: any

            res = await addUser(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    const updateUser: any = await userRegistrationDeactivation(
                        updateUserActivation
                    )

                    if (updateUser) {
                        setToastMessage(
                            `User: ${data.userEmail} added successfully`,
                            'success'
                        )

                        onClose()
                    }
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const emailCheck = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(userEmail)
    }

    const handleReset = () =>
        setState({
            formData: defaultState.formData,
            phoneNumberError: defaultState.phoneNumberError,

            formValidation: defaultState.formValidation,
        })

    useEffect(() => {
        getData()
    }, [])

    useEffect(() => {
        const parsedInfo = parsePhoneNumber(userPhoneNumber, {
            defaultCountry: 'US',
            extract: false,
        })

        const country = parsedInfo?.country || 'US'

        const parsedNumber = new AsYouType(country).input(userPhoneNumber)
        const isValid = parsedInfo?.isValid()

        if (userPhoneNumber && userPhoneNumber !== '') {
            setState({
                formData: { ...formData, userPhoneNumber: parsedNumber },
                phoneNumberError: !isValid,
            })
        }
    }, [userPhoneNumber])

    useEffect(() => {
        if (!userProfile) return

        let assignedManufacturers: any
        let assignedManufacturersOptions: any

        if (userProfile.dealerProfile === true) {
            const findDealer = dealers?.find(
                ({ dealerName }) => dealerName === assignedDealer
            )

            assignedManufacturers = findDealer?.manufacturer

            assignedManufacturersOptions = assignedManufacturers?.map(
                (manufacturer) => manufacturer?.manufacturerName
            )
        } else {
            assignedManufacturers =
                userProfile?.userProfileAssignedManufacturers

            assignedManufacturersOptions = assignedManufacturers?.map(
                ({ manufacturerName }) => manufacturerName
            )
        }

        setState({ assignedManufacturers, assignedManufacturersOptions })
    }, [userProfile, dealers, assignedDealer, activeManufacturer])

    useEffect(() => {
        const emailValid = () => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(userEmail)
        }

        if (
            isFormDirty &&
            !userEmailValidation &&
            emailValid() &&
            !userFirstNameValidation &&
            !userLastNameValidation &&
            !manufacturerValidation &&
            !activeManufacturerValidation &&
            !userProfileValidation &&
            userEmail &&
            userFirstName &&
            userLastName &&
            userProfile?.userProfileName &&
            activeManufacturer
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        userEmailValidation,
        userFirstNameValidation,
        userLastNameValidation,

        userProfileValidation,
        activeManufacturerValidation,
        userEmail,
        userFirstName,
        userLastName,

        activeManufacturer,
        userProfile,
        password,
    ])

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ marginTop: 5 }}>
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        md={
                            auth?.userProfile?.superAdmin &&
                            auth?.userProfile?.superAdmin === true
                                ? 7
                                : 12
                        }
                    >
                        <FormSection
                            title="User Information"
                            titleAlignment="center"
                        >
                            <Grid item md={7} xs={12}>
                                <TextField
                                    required={true}
                                    label="E-Mail"
                                    name="userEmail"
                                    fullWidth
                                    onChange={handleChange}
                                    value={userEmail}
                                    onBlur={validateOnBlur}
                                    disabled={permissionCheck}
                                    error={
                                        userEmailValidation === false &&
                                        userEmail
                                            ? !emailCheck()
                                            : userEmailValidation && !userEmail
                                    }
                                    helperText={
                                        userEmailValidation === false &&
                                        !userEmail
                                            ? userEmailValidation && !userEmail
                                            : !emailCheck()
                                            ? 'Please enter a valid Email Address.'
                                            : ''
                                    }
                                />
                            </Grid>

                            <Grid item md={5} xs={12}>
                                <TextField
                                    required={true}
                                    label="Enter User Temporary Password."
                                    name="password"
                                    fullWidth
                                    onChange={handleChange}
                                    value={password}
                                    type="password"
                                    onBlur={validateOnBlur}
                                    disabled={permissionCheck}
                                    error={passwordValidation && !password}
                                    helperText={
                                        passwordValidation && !password
                                            ? 'Please enter a password'
                                            : ''
                                    }
                                />
                            </Grid>

                            <Grid item md={4} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    label="First Name"
                                    name="userFirstName"
                                    onChange={handleChange}
                                    value={userFirstName}
                                    onBlur={validateOnBlur}
                                    error={
                                        userFirstNameValidation &&
                                        !userFirstName
                                    }
                                    helperText={
                                        userFirstNameValidation &&
                                        !userFirstName
                                            ? 'Please enter a First Name'
                                            : ''
                                    }
                                    disabled={permissionCheck}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    label="Last Name"
                                    name="userLastName"
                                    onChange={handleChange}
                                    value={userLastName}
                                    onBlur={validateOnBlur}
                                    error={
                                        userLastNameValidation && !userLastName
                                    }
                                    helperText={
                                        userLastNameValidation && !userLastName
                                            ? 'Please enter a Last Name'
                                            : ''
                                    }
                                    disabled={permissionCheck}
                                />
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="userPhoneNumber"
                                    onChange={handleChange}
                                    value={userPhoneNumber}
                                    error={
                                        (userPhoneNumberValidation &&
                                            !userPhoneNumber) ||
                                        (phoneNumberError &&
                                            userPhoneNumber?.length !== '')
                                    }
                                    helperText={
                                        phoneNumberError &&
                                        userPhoneNumber?.length !== ''
                                            ? 'Please Enter a Valid Phone Number'
                                            : userPhoneNumberValidation &&
                                              !userPhoneNumber
                                            ? 'Please enter a Phone Number for the User'
                                            : ''
                                    }
                                    disabled={permissionCheck}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <UniversalGoogleMapsAc
                                    name="userAddress"
                                    value={userAddress}
                                    label="Address"
                                    fullWidth={true}
                                    clearFunction={() =>
                                        setState({
                                            formValidation: {
                                                ...formValidation,
                                                isFormDirty: true,
                                            },
                                            formData: {
                                                ...formData,
                                                userAddress: '',
                                            },
                                        })
                                    }
                                    onChange={(event) => {
                                        if (event?.address) {
                                            setState({
                                                formData: {
                                                    ...formData,
                                                    userAddress: event.address,
                                                },
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                    }}
                                    disabled={permissionCheck}
                                />
                            </Grid>
                        </FormSection>
                        <FormSection
                            title="Boat Information"
                            titleAlignment="center"
                        >
                            <Grid item md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required={true}
                                        fullWidth
                                        name="fullHin"
                                        label="HIN"
                                        disabled={
                                            permissionCheck === true
                                                ? true
                                                : !!row.fullHin
                                        }
                                        value={fullHin}
                                        onChange={handleChange}
                                        onBlur={validateOnBlur}
                                        error={
                                            fullHinValidation &&
                                            fullHin.length !== 14
                                        }
                                        helperText={
                                            fullHinValidation &&
                                            fullHin.length !== 14
                                                ? 'Full HIN must be 14 characters long.'
                                                : ''
                                        }
                                        inputProps={{ maxLength: 14 }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                marginTop: 0.5,
                                                display: fullHinCharacterCount
                                                    ? ''
                                                    : 'none',
                                            }}
                                            fontSize={'small'}
                                            color={
                                                fullHinCharacterCount.length ===
                                                14
                                                    ? 'primary'
                                                    : '#AEAEAE'
                                            }
                                        >
                                            {`${
                                                fullHinCharacterCount.length
                                            } / ${14} Characters`}
                                        </Typography>
                                    </Box>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    name="boatModel"
                                    label="Boat Model"
                                    disabled={true}
                                    value={boatModel}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    name="manufacturer"
                                    label="Boat Manufacturer"
                                    disabled={true}
                                    value={manufacturer}
                                />
                            </Grid>
                        </FormSection>
                    </Grid>
                    {auth?.userProfile?.superAdmin &&
                        auth?.userProfile?.superAdmin === true && (
                            <Grid item xs={12} md={5}>
                                <FormSection
                                    title="User Profile"
                                    titleAlignment="center"
                                >
                                    <Grid item xs={12}>
                                        <UniversalAutocomplete
                                            options={userProfiles.map(
                                                ({ userProfileName }) =>
                                                    userProfileName
                                            )}
                                            value={userProfile?.userProfileName}
                                            handleChange={handleChange}
                                            required={true}
                                            label="User Profile"
                                            name="userProfile"
                                            onBlur={validateOnBlur}
                                            disabled={
                                                !auth?.userProfile?.superAdmin
                                            }
                                            error={
                                                userProfileValidation &&
                                                !userProfile?.userProfileName
                                            }
                                            helperText={
                                                userProfileValidation &&
                                                !userProfile?.userProfileName
                                                    ? 'Please select a user profile.'
                                                    : ''
                                            }
                                            clearFunction={() =>
                                                setState({
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                    formData: {
                                                        ...formData,
                                                        userProfile: {},
                                                        activeManufacturer: '',
                                                    },
                                                })
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {userProfile?.dealerProfile ===
                                            true && (
                                            <Autocomplete
                                                id="assignedDealer"
                                                value={assignedDealer}
                                                onBlur={() =>
                                                    setState({
                                                        formData: {
                                                            ...formData,
                                                            activeManufacturer:
                                                                '',
                                                        },
                                                    })
                                                }
                                                onChange={(event, newValue) => {
                                                    const data = {
                                                        target: {
                                                            name: 'assignedDealer',
                                                            value: newValue,
                                                        },
                                                    }

                                                    handleChange(data)
                                                }}
                                                options={dealerOptions}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        name="assignedDealer"
                                                        label="Assigned Dealer"
                                                        placeholder="User Profile Assigned Dealer"
                                                        onBlur={validateOnBlur}
                                                        helperText={
                                                            assignedDealerValidation &&
                                                            !assignedDealer
                                                                ? 'Please select a Dealer'
                                                                : ''
                                                        }
                                                        error={
                                                            assignedDealerValidation &&
                                                            !assignedDealer
                                                        }
                                                        required={true}
                                                    />
                                                )}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        {userProfile?.userProfileName && (
                                            <UniversalAutocomplete
                                                options={
                                                    assignedManufacturersOptions ||
                                                    []
                                                }
                                                disabled={
                                                    permissionCheck ||
                                                    userProfile?.dealerProfile
                                                        ? !assignedDealer
                                                        : false
                                                }
                                                value={activeManufacturer}
                                                handleChange={handleChange}
                                                onBlur={validateOnBlur}
                                                name="activeManufacturer"
                                                label="Active Manufacturer"
                                                required={true}
                                                error={
                                                    activeManufacturerValidation &&
                                                    !activeManufacturer
                                                }
                                                helperText={
                                                    activeManufacturerValidation &&
                                                    !activeManufacturer
                                                        ? 'Please select an Active Manufacturer.'
                                                        : ''
                                                }
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sx={{ marginTop: -8 }}>
                                        {userProfile &&
                                            userProfile?.userProfileName && (
                                                <UserProfileForm
                                                    row={userProfile}
                                                    onClose={() => {}}
                                                    readOnly={true}
                                                    hideDisplay={
                                                        userProfile?.dealerProfile
                                                    }
                                                />
                                            )}
                                    </Grid>
                                </FormSection>
                            </Grid>
                        )}
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <SaveButton
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    disabledSave={!isFormValid || phoneNumberError}
                    disabledReset={!isFormDirty}
                    saveTitle={!userAccount ? 'Save & Close' : 'Save'}
                />
            </Grid>
        </Grid>
    )
}

export default UserRegistrationForm
